import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useProductStore } from "./store/productStore";
import { useAuth } from "./AuthContext";
import AdminProductForm from "./components/AdminProductForm";
import AdminProductList from "./components/AdminProductList";
import AdminMessages from "./AdminMessages";
import AdminCatering from "./components/AdminCatering";
import AdminReviews from "./components/AdminReviews";

import AdminOrders from "./AdminOrders";
import AdminInvoices from "./AdminInvoices";
import TaxReports from "./components/TaxReports";
import AdminCreateOrder from "./AdminCreateOrder";
import { CheckCircle, ShoppingCart } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { products, loading, error, fetchProducts, addProduct, updateProduct, deleteProduct } = useProductStore();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentView, setCurrentView] = useState('products'); // 'products', 'messages', 'catering', 'reviews', 'orders', 'invoices', 'tax-reports', 'create-order'
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    quantity: "",
    description: "",
    image: null,
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    // Initial fetch
    console.log("Fetching initial products...");
    setPage(1);
    setHasMore(true);
    fetchProducts({ page: 1, limit: ITEMS_PER_PAGE }, false).then(data => {
        console.log("Initial products fetched:", data);
        if (data && data.length < ITEMS_PER_PAGE) {
            setHasMore(false);
        }
    }).catch(err => console.error("Error fetching initial products:", err));
  }, [fetchProducts]);

  const loadMore = async () => {
      console.log("Loading more products...");
      const nextPage = page + 1;
      const data = await fetchProducts({ page: nextPage, limit: ITEMS_PER_PAGE }, true);
      console.log("More products fetched:", data);
      setPage(nextPage);
      if (data && data.length < ITEMS_PER_PAGE) {
          setHasMore(false);
      }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      if (editingId) {
        await updateProduct(editingId, {
          ...formData,
          price: parseFloat(formData.price),
          quantity: parseInt(formData.quantity) || 0,
        });
        setShowSuccessModal(true);
        setEditingId(null);
      } else {
        await addProduct({
          ...formData,
          price: parseFloat(formData.price),
          quantity: parseInt(formData.quantity) || 0,
        });
        setShowSuccessModal(true);
      }

      setFormData({
        name: "",
        price: "",
        quantity: "",
        description: "",
        image: null,
      });
      setShowForm(false);
    } catch (error) {
      toast.error("Error saving product: " + error.message);
      console.error("Error saving product:", error);
    }
  };

  const handleEdit = (product) => {
    setFormData(product);
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: "",
      price: "",
      quantity: "",
      description: "",
      image: null,
    });
  };

  const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this product?")) {
          await deleteProduct(id);
      }
  }

  if (!user?.isAdmin) {
    return (
      <div className="text-center py-16">
        <p className="text-xl text-red-600 dark:text-red-400">
          Access Denied. Admin privileges required.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-4xl font-bold mb-8">Admin Dashboard</h1>

      <div className="flex flex-wrap gap-4 mb-8">
        <button
          onClick={() => setCurrentView('products')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'products' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          Manage Products
        </button>
        <button
          onClick={() => setCurrentView('messages')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'messages' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          View Messages
        </button>
        <button
          onClick={() => setCurrentView('orders')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'orders' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          View All Orders
        </button>
        <button
          onClick={() => setCurrentView('create-order')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'create-order' ? 'bg-black text-white' : 'bg-raya-bg-off text-raya-text-secondary border border-raya-border'
          }`}
        >
          <ShoppingCart size={18} />
          Quick Order (POS)
        </button>
        <button
          onClick={() => setCurrentView('catering')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'catering' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          Catering Orders
        </button>
        <button
          onClick={() => setCurrentView('invoices')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'invoices' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          Manage Invoices
        </button>
        <button
          onClick={() => setCurrentView('tax-reports')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'tax-reports' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          Tax Compliance
        </button>
        <button
          onClick={() => setCurrentView('reviews')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            currentView === 'reviews' ? 'bg-raya-accent text-black' : 'bg-raya-bg-off text-raya-text-secondary'
          }`}
        >
          Manage Reviews
        </button>
      </div>

      {currentView === 'products' && (
        <div className="mb-8">
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-raya-accent hover:bg-raya-accent-hover text-black px-6 py-3 rounded-lg font-semibold transition"
          >
            {showForm ? "Cancel" : "Add New Product"}
          </button>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 bg-raya-bg/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-raya-card rounded-lg p-8 max-w-md w-full shadow-2xl border border-raya-border">
            <div className="flex items-center justify-center w-16 h-16 bg-raya-accent/10 rounded-full mx-auto mb-4 text-raya-accent">
              <CheckCircle size={40} />
            </div>
            <h3 className="text-2xl font-bold text-center mb-2 text-raya-text-primary">Success!</h3>
            <p className="text-raya-text-secondary text-center mb-6">
              Product has been successfully saved.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-raya-accent hover:bg-raya-accent-hover text-black py-3 rounded-lg font-semibold transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <AdminProductForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={!!editingId}
        />
      )}

      {currentView === 'messages' && <AdminMessages />}
      {currentView === 'orders' && <AdminOrders embedded={true} />}
      {currentView === 'catering' && <AdminCatering />}
      {currentView === 'reviews' && <AdminReviews />}
      {currentView === 'invoices' && <AdminInvoices />}
      {currentView === 'tax-reports' && <TaxReports />}
      {currentView === 'create-order' && <AdminCreateOrder />}
      
      {currentView === 'products' && (
        <>
        <AdminProductList
          products={products}
          loading={loading}
          error={error}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRetry={fetchProducts}
        />
        {hasMore && !loading && (
            <div className="flex justify-center mt-8">
                <button
                    onClick={loadMore}
                    className="bg-raya-accent hover:bg-raya-accent-hover text-black font-bold py-2 px-6 rounded-full transition shadow-lg"
                >
                    Load More Products
                </button>
            </div>
        )}
        {loading && products.length > 0 && (
            <div className="flex justify-center mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
            </div>
        )}
        </>
      )}
    </div>
  );
}
